/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceEventEmitter, View } from 'react-native';
import debounce from 'lodash/debounce';
import { EmptyStateView } from '@airasia-rn/social';
import { LoggerProviderType, useLogger } from '@airasia-rn/core-logger';
import {
  HomePageEvents,
  trackRecommendationShowMoreClicked,
} from '../../analytics/homepage';
import RecommendedTabView, {
  getDefaultRecommendationTabIndex,
  type RecommendedTabViewRef,
} from '../../components/RecommendationTabsView/RecommendationTabsView';
import { useRecommendationBookingState } from '../../hooks/useRecommendationBookingState';
import { useHasUpcomingBooking } from '../../hooks/useHasUpcomingBooking';
import type { RecommendationTabsBlock } from '../../models/homepageBlockTypes';
import noInternetImage from '../../assets/images/img_no_internet.png';
import { styles } from './HomePage.styles';
import type { HomePageProps } from './HomePage';
import { useHomeNavigation } from '../../contexts/HomeNavigationContext';

// Recommendation owns its tab selection, booking-default, offline, and
// show-more wiring because both native sticky headers and web content render the
// same business section. HomePage only decides where this section sits in the
// list, which keeps hooks out of the platform page files.
export interface HomePageRecommendationSectionRef {
  maybeLoadMoreOnScroll: () => void;
}

export type RecommendationTabIndexByUuid = Record<string, number>;

interface HomePageRecommendationSectionProps {
  block: RecommendationTabsBlock;
  mode: 'offline' | 'tabBar' | 'content';
  isMovementsEnabled: boolean;
  recommendationTabIndexByUuid: RecommendationTabIndexByUuid;
  setRecommendationTabIndexByUuid: React.Dispatch<
    React.SetStateAction<RecommendationTabIndexByUuid>
  >;
  openModule?: HomePageProps['openModule'];
}

const SWITCH_RECOMMENDATION_TAB_EVENT = 'switchRecommendationTab';

const HomePageRecommendationSection = forwardRef<
  HomePageRecommendationSectionRef,
  HomePageRecommendationSectionProps
>(function HomePageRecommendationSection(
  {
    block,
    mode,
    isMovementsEnabled,
    recommendationTabIndexByUuid,
    setRecommendationTabIndexByUuid,
    openModule,
  },
  ref,
) {
  const recommendedTabRef = useRef<RecommendedTabViewRef>(null);
  const { t } = useTranslation('home');
  const { logEvent } = useLogger();
  const {
    hasUpcomingBooking,
    isLoading: isUpcomingBookingLoading,
    isError: isUpcomingBookingError,
  } = useHasUpcomingBooking();
  const isUpcomingBookingReady =
    !isUpcomingBookingLoading && !isUpcomingBookingError;
  const { persistedHasUpcomingBooking } = useRecommendationBookingState({
    hasUpcomingBooking,
    shouldPersist: isUpcomingBookingReady,
  });

  const recommendationTabs = useMemo(
    () =>
      isMovementsEnabled
        ? block.data.tabs
        : block.data.tabs.filter(
            (tab) => String(tab.id).toLowerCase() !== 'movements',
          ),
    [block.data.tabs, isMovementsEnabled],
  );

  const { recommendationActions: { onCardPress, onPressCta } = {} } =
    useHomeNavigation();

  const controlledTabIndex = useMemo(() => {
    const inMemoryIndex = recommendationTabIndexByUuid[block.uuid];
    if (inMemoryIndex !== undefined) return inMemoryIndex;

    const bookingStateForDefault = isUpcomingBookingReady
      ? hasUpcomingBooking
      : persistedHasUpcomingBooking;

    return getDefaultRecommendationTabIndex(recommendationTabs, {
      hasUpcomingBooking: bookingStateForDefault,
      isMovementsEnabled: recommendationTabs.some(
        (tab) => String(tab.id).toLowerCase() === 'movements',
      ),
    });
  }, [
    block.uuid,
    hasUpcomingBooking,
    isUpcomingBookingReady,
    persistedHasUpcomingBooking,
    recommendationTabIndexByUuid,
    recommendationTabs,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      maybeLoadMoreOnScroll: () =>
        recommendedTabRef.current?.maybeLoadMoreOnScroll(),
    }),
    [],
  );

  const handleTabIndexChange = useCallback(
    (index: number) => {
      setRecommendationTabIndexByUuid((prev) =>
        prev[block.uuid] === index ? prev : { ...prev, [block.uuid]: index },
      );
    },
    [block.uuid, setRecommendationTabIndexByUuid],
  );

  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      SWITCH_RECOMMENDATION_TAB_EVENT,
      (actionParam: string) => {
        const parts = actionParam.split('/');
        if (
          parts.length < 3 ||
          parts[0] !== 'home' ||
          parts[1] !== 'recommendation'
        ) {
          return;
        }

        const tabId = parts[2];
        const targetIndex = recommendationTabs.findIndex(
          (t) => String(t.id).toLowerCase() === tabId.toLowerCase(),
        );
        if (targetIndex !== -1) {
          handleTabIndexChange(targetIndex);
        }
      },
    );
    return () => subscription.remove();
  }, [recommendationTabs, handleTabIndexChange]);

  const stableRecommendationCallbacks = useMemo(() => {
    if (!onCardPress && !onPressCta) {
      return undefined;
    }

    return {
      onCardPress: onCardPress
        ? debounce(onCardPress, 500, {
            leading: true,
            trailing: false,
          })
        : undefined,
      onCtaPress: onPressCta
        ? debounce(onPressCta, 500, {
            leading: true,
            trailing: false,
          })
        : undefined,
    };
  }, [onCardPress, onPressCta]);

  const handleForYouShowMorePress = useCallback(
    (params?: { tabName: string; quartile: number; maxIndex: number }) => {
      if (params) {
        logEvent(
          HomePageEvents.tap,
          trackRecommendationShowMoreClicked(params),
          LoggerProviderType.all,
        );
      }
    },
    [logEvent],
  );

  if (mode === 'offline') {
    return (
      <View style={styles.offlineRecommendation}>
        <EmptyStateView
          image={noInternetImage}
          title={t('homePage.offlineTitle')}
          description={t('homePage.offlineDescription')}
        />
      </View>
    );
  }

  if (mode === 'tabBar') {
    return (
      <View style={styles.recommendationStickyHeader}>
        <RecommendedTabView
          ref={recommendedTabRef}
          tabs={recommendationTabs}
          renderMode="tabBar"
          controlledTabIndex={controlledTabIndex}
          recommendationCallbacks={stableRecommendationCallbacks}
          onTabIndexChange={handleTabIndexChange}
        />
      </View>
    );
  }

  return (
    <RecommendedTabView
      ref={recommendedTabRef}
      tabs={recommendationTabs}
      renderMode="content"
      controlledTabIndex={controlledTabIndex}
      onTabIndexChange={handleTabIndexChange}
      recommendationCallbacks={stableRecommendationCallbacks}
      openModule={openModule}
      onForYouShowMorePress={handleForYouShowMorePress}
    />
  );
});

export default memo(HomePageRecommendationSection);
